import { AnimatePresence, motion } from 'framer-motion';
import Loader from 'molecules/Loader';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectWorkspaceId } from 'store/userSlice';
import { fetchWorkspace, fetchWorkspaceVersion } from 'store/workspaceThunks';

import ExecutePage from 'containers/Execute';
import PlanPage from 'containers/Plan';
import { AppDispatch } from 'store';
import {
    closeModal,
    LayoutState,
    selectLayout,
    selectModals,
} from 'store/layoutSlice';
import { selectWorkspaceData } from 'store/workspaceSlice';

type WorkspaceState = 'EXECUTE' | 'PLAN';

const Workspace = (props: { workspaceState: WorkspaceState }) => {
    let navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const workspaceData = useSelector(selectWorkspaceData);
    const layoutState: LayoutState = useSelector(selectLayout);
    const modals = useSelector(selectModals);
    const workspaceId = useSelector(selectWorkspaceId);
    const workspaceState = props.workspaceState;
    const workspaceStatus = workspaceData.status.fetchWorkspace;
    const currentVersion = useRef<number | null>(null);

    const handleKeyDown = useCallback(
        (event: any) => {
            // Don't trigger shortcuts when typing in input fields
            const activeElement = document.activeElement;
            const isTyping =
                activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement;

            if (event.key === '1' && !isTyping) {
                navigate('/plan');
            } else if (event.key === '2' && !isTyping) {
                navigate('/execute');
            } else if ((event.key === 'q' || event.key === 'Q') && !isTyping) {
                // Prevent the 'q' from being typed
                event.preventDefault();
                // Trigger search wrapper's expand function
                const searchWrapper = document.querySelector('[data-search-wrapper]') as any;
                if (searchWrapper && searchWrapper.triggerSearch) {
                    searchWrapper.triggerSearch();
                }
            }
        },
        [navigate]
    );

    const handleBackButton = useCallback((event: PopStateEvent) => {
        // Check if there are any open modals
        if (modals.length > 0) {
            // Prevent navigation
            event.preventDefault();
            // Close the topmost modal (last in the array)
            const topModal = modals[modals.length - 1];
            dispatch(closeModal({ name: topModal.name }));
            // Push the current state back to prevent navigation
            window.history.pushState(null, '', window.location.pathname);
            return;
        }

        // Check if there's an active input or textarea
        const activeElement = document.activeElement;
        if (
            activeElement instanceof HTMLInputElement ||
            activeElement instanceof HTMLTextAreaElement
        ) {
            // Prevent navigation
            event.preventDefault();
            // Blur the active element (same as pressing Escape)
            activeElement.blur();
            // Push the current state back to prevent navigation
            window.history.pushState(null, '', window.location.pathname);
        }
    }, [modals, dispatch]);

    const checkWorkspaceVersion = useCallback(async () => {
        if (!workspaceId || workspaceStatus !== 'SUCCEEDED') return;

        try {
            const versionResponse = await dispatch(
                fetchWorkspaceVersion()
            ).unwrap();
            const newVersion = versionResponse.version;
            const oldVersion = currentVersion.current;

            console.log(
                'Version check - Current:',
                oldVersion,
                'New:',
                newVersion
            );

            if (oldVersion !== null && oldVersion !== newVersion) {
                console.log('Version changed, refreshing workspace...');
                currentVersion.current = newVersion;
                await dispatch(
                    fetchWorkspace({ workspaceId, isRefresh: true })
                );
            } else if (oldVersion === null) {
                currentVersion.current = newVersion;
            }
        } catch (error) {
            console.error('Failed to check workspace version:', error);
        }
    }, [dispatch, workspaceId, workspaceStatus]);

    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible') {
            checkWorkspaceVersion();
        }
    }, [checkWorkspaceVersion]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        // Add a history entry to intercept back button
        window.history.pushState(null, '', window.location.pathname);

        window.addEventListener('popstate', handleBackButton);
        return () => {
            window.removeEventListener('popstate', handleBackButton);
        };
    }, [handleBackButton]);

    useEffect(() => {
        if (workspaceId) {
            dispatch(fetchWorkspace({ workspaceId }));
            dispatch(fetchWorkspaceVersion()).then((result) => {
                if (
                    result.type === 'workspace/fetchWorkspaceVersion/fulfilled'
                ) {
                    currentVersion.current = (result.payload as any).version;
                }
            });
        }
    }, [dispatch, workspaceId]);

    useEffect(() => {
        layoutState.projectsView === 'list'
            ? document.body.classList.remove('grid-view')
            : document.body.classList.add('grid-view');
    }, [layoutState.projectsView]);

    useEffect(() => {
        layoutState.theme === 'light'
            ? document.body.classList.remove('dark-theme')
            : document.body.classList.add('dark-theme');
    }, [layoutState.theme]);

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
        };
    }, [handleVisibilityChange]);

    if (workspaceStatus === 'IDLE' || workspaceStatus === 'LOADING') {
        return <Loader />;
    }

    if (workspaceStatus === 'FAILED') {
        return (
            <div style={{ margin: '32px' }}>
                <p>
                    Nieznany bląd aplikacji:{' '}
                    {workspaceData?.error.fetchWorkspace}
                </p>
            </div>
        );
    }

    if (workspaceStatus === 'SUCCEEDED' || workspaceStatus === 'REFRESHING') {
        const isMobile = window.innerWidth < 768;
        const slideDistance = isMobile ? '100%' : '100px';

        return (
            <>
                <AnimatePresence initial={false} mode="popLayout">
                    {workspaceState === 'PLAN' && (
                        <motion.div
                            key="plan"
                            style={{ width: '100%' }}
                            initial={{ x: `-${slideDistance}`, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: `-${slideDistance}`, opacity: 0 }}
                            transition={{ duration: 0.45, ease: 'easeInOut' }}
                        >
                            <PlanPage />
                        </motion.div>
                    )}
                    {workspaceState === 'EXECUTE' && (
                        <motion.div
                            key="execute"
                            style={{ width: '100%' }}
                            initial={{ x: slideDistance, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: slideDistance, opacity: 0 }}
                            transition={{ duration: 0.45, ease: 'easeInOut' }}
                        >
                            <ExecutePage />
                        </motion.div>
                    )}
                </AnimatePresence>
                {workspaceStatus === 'REFRESHING' && <Loader overlay />}
            </>
        );
    }

    return <div>error</div>;
};

export default Workspace;
